import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import configService from '../services/config';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../redux/reducers/authSlice';
import toast from 'react-hot-toast';
import { setCompanyAddress, setCompanyId, setCompanyName, setUserId } from '../redux/reducers/GlobalVariablesSlice';
import siteIcon from '../Assets/Images/siteIcon-32.png';

export default function LoginPage() {
  const [email, setEmailInput] = useState('');
  const [mobileNo, setMobileNoInput] = useState('');
  const [userId, setUserIdInput] = useState('');
  const [password, setPasswordInput] = useState('');
  const [credentialType, setCredentialType] = useState('email'); // 'email', 'mobile', 'userid'
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const validate = () => {
    const e = {};
    if (credentialType === 'email') {
      if (!email) e.credential = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.credential = 'Enter a valid email';
    } else if (credentialType === 'mobile') {
      if (!mobileNo) e.credential = 'Mobile number is required';
      else if (!/^\d{10}$/.test(mobileNo.replace(/\D/g, ''))) e.credential = 'Enter a valid 10-digit mobile number';
    } else if (credentialType === 'userid') {
      if (!userId) e.credential = 'User ID is required';
      else if (userId.trim().length < 3) e.credential = 'User ID must be at least 3 characters';
    }
    if (!password) e.password = 'Password is required';
    else if (password.length < 6) e.password = 'Minimum 6 characters required';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const payload = {
        password,
        isOrganisationUser: true,
      };
      if (credentialType === 'email') payload.email = email;
      else if (credentialType === 'mobile') payload.mobileNo = mobileNo;
      else if (credentialType === 'userid') payload.email = userId;

      const res = await configService.signin(payload);

      if (res.status === 200) {

        const userData = res.data;
        console.log('Login successful:', userData);
        dispatch(setCompanyId(userData.company_id));
        dispatch(setCompanyAddress(userData.company_address));
        dispatch(setUserId(userData.userId));
        dispatch(setCompanyName(userData.company_name));

        dispatch(
          loginSuccess({
            token: userData.token,
            user: userData,
          })
        );

        // alert("Login successful 🚀");
        navigate("/dashboard/dashboard");
      }

    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      <div className="login-bg">
        <div className="login-orb orb1" />
        <div className="login-orb orb2" />
        <div className="login-grid" />
      </div>

      <div className="login-panel fade-in-up">
        <div className="login-brand">
          <div className="login-logo">
            <img
              src={siteIcon}
              alt="D-Track"
              width="28"
              height="28"
              style={{ borderRadius: '50%', objectFit: 'cover' }}
            />
          </div>
          <span className="login-brand-name">D-Track</span>
        </div>

        <h1 className="login-title">Welcome back</h1>
        <p className="login-subtitle">Sign in to your employee portal</p>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {errors.general && (
            <div className="login-error-banner">
              <span>⚠</span> {errors.general}
            </div>
          )}

          <div className="login-field">
            <label className="login-label">Login with</label>
            <div className="login-credential-tabs">
              <button
                type="button"
                className={`login-tab ${credentialType === 'email' ? 'active' : ''}`}
                onClick={() => {
                  setCredentialType('email');
                  setErrors({});
                }}
              >
                Email
              </button>
              <button
                type="button"
                className={`login-tab ${credentialType === 'mobile' ? 'active' : ''}`}
                onClick={() => {
                  setCredentialType('mobile');
                  setErrors({});
                }}
              >
                Mobile
              </button>
              <button
                type="button"
                className={`login-tab ${credentialType === 'userid' ? 'active' : ''}`}
                onClick={() => {
                  setCredentialType('userid');
                  setErrors({});
                }}
              >
                User ID
              </button>
            </div>
          </div>

          <div className={`login-field ${errors.credential ? 'has-error' : ''}`}>
            <label className="login-label">
              {credentialType === 'email' && 'Email address'}
              {credentialType === 'mobile' && 'Mobile number'}
              {credentialType === 'userid' && 'User ID'}
            </label>
            <div className="login-input-wrap">
              <span className="login-input-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M1 6l7 4 7-4" stroke="currentColor" strokeWidth="1.4" />
                </svg>
              </span>
              {credentialType === 'email' && (
                <input
                  type="email"
                  className="login-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmailInput(e.target.value)}
                  autoComplete="email"
                />
              )}
              {credentialType === 'mobile' && (
                <input
                  type="tel"
                  className="login-input"
                  placeholder="+91 98765 43210"
                  value={mobileNo}
                  onChange={e => setMobileNoInput(e.target.value)}
                  autoComplete="tel"
                />
              )}
              {credentialType === 'userid' && (
                <input
                  type="text"
                  className="login-input"
                  placeholder="Enter your User ID"
                  value={userId}
                  onChange={e => setUserIdInput(e.target.value)}
                  autoComplete="username"
                />
              )}
            </div>
            {errors.credential && <span className="login-err">{errors.credential}</span>}
          </div>

          <div className={`login-field ${errors.password ? 'has-error' : ''}`}>
            <label className="login-label">Password</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M5 7V5a3 3 0 1 1 6 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </span>
              <input
                type={showPass ? 'text' : 'password'}
                className="login-input"
                placeholder="••••••••"
                value={password}
                onChange={e => setPasswordInput(e.target.value)}
                autoComplete="current-password"
              />
              <button type="button" className="login-toggle-pass" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                {showPass ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.4" /><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" /><path d="M2 2l12 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.4" /><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" /></svg>
                )}
              </button>
            </div>
            {errors.password && <span className="login-err">{errors.password}</span>}
          </div>

          <button type="submit" className={`login-btn ${loading ? 'loading' : ''}`} disabled={loading}>
            {loading ? (
              <span className="login-spinner" />
            ) : (
              <>Sign in <span className="login-btn-arrow">→</span></>
            )}
          </button>
        </form>

        <p className="login-hint">Demo: any valid email + 6-char password</p>
      </div>
    </div>
  );
}